// Components==============
import NavigationBox, {
    Displaymode,
} from 'components/Layout/NavigationBox/NavigationBox';
import ProgressBar from 'components/UI/ProgressBar/ProgressBar';
import React, { useContext, useEffect, useState } from 'react';
import Confetti from 'react-dom-confetti';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { GlobalContext } from 'utilities/GlobalContext';
import { getExerciseLength } from 'utilities/getExerciseLength';
import { HomeworkContext, SequenceOptions } from '../Homework';
// =========================

const titles = {
    terugblik: 'Terugblik',
    doel: 'Doel van de les',
    voorkennis: 'Voorkennis ophalen',
    instructie: 'Instructie',
    oefening: 'Begeleide inoefening',
    succescriteria: 'Doelen en succescriteria',
    afgerond: 'Afgerond!',
};

// these titles are used for the special Summer Treatments
const summerTitles = {
    terugblik: 'Uitleg',
    doel: 'Opdracht 1',
    voorkennis: 'Opdracht 2',
    instructie: 'Opdracht 3',
    oefening: 'Opdracht 4',
    succescriteria: 'Topper!',
    afgerond: 'Afgerond!',
};

export default function NavigationSequence() {
    const {
        homework,
        sequence,
        setSequence,
        displayPriorKnowledge,
        displayInstruction,
        displayMain,
        priorKnowledgeStatus,
        instructionStatus,
        mainExerciseStatus,
        priorKnowledgeIsFinished,
        instructionIsFinished,
        mainIsFinished,
        priorKnowledgeWasEmpty,
        instructionWasEmpty,
        mainWasEmpty,
        priorKnowledgeExercise,
        instructionExercise,
        mainExercise,
        progress,
        setProgress,
    } = useContext(HomeworkContext);
    const { setKeyboardIsOpen } = useContext(GlobalContext);

    const [displayMode, setDisplayMode] = useState<Displaymode>('centered');
    const [top, setTop] = useState('50%');

    const treatment = useSelector((state: ReduxStore) => {
        if (homework?.treatment_id)
            return state?.treatment?.entities.byId[homework?.treatment_id];
        return undefined;
    });

    // get treatmentID for special Summer Treatments
    const { treatmentId } = useParams() as {
        treatmentId: string;
    };

    // add flag for special Summer Treatments
    let isSummerTreatment = false;
    if (
        treatmentId === '71' ||
        treatmentId === '72' ||
        treatmentId === '80' ||
        treatmentId === '81'
    ) {
        // check for special Summer Treatments
        isSummerTreatment = true;
    }

    useEffect(() => {
        if (displayPriorKnowledge || displayInstruction || displayMain) {
            setDisplayMode('centered-y');
            setTop('2em');
        }

        if (!displayPriorKnowledge && !displayInstruction && !displayMain) {
            setTop('50%');
            setDisplayMode('centered');
        }
    }, [displayPriorKnowledge, displayInstruction, displayMain]);

    const [
        temporarySequence,
        setTemporarySequence,
    ] = useState<SequenceOptions | null>(null);

    const conditionalSequence = temporarySequence || sequence;

    const priorKnowledgeIsFinishedCondition =
        priorKnowledgeIsFinished || priorKnowledgeStatus === 'finished';

    const instructionIsFinishedCondition =
        instructionIsFinished || instructionStatus === 'finished';

    const mainIsFinishedCondition =
        mainIsFinished || mainExerciseStatus === 'finished';

    const previousSequence = () => {
        const keys = Object.keys(titles) as SequenceOptions[];
        const prevIndex = keys.indexOf(conditionalSequence) - 1;
        const prevItem = keys[prevIndex];

        if (
            sequence === 'voorkennis' &&
            priorKnowledgeStatus === 'started' &&
            !priorKnowledgeWasEmpty
        )
            return setTemporarySequence(prevItem);

        if (
            sequence === 'instructie' &&
            instructionStatus === 'started' &&
            !instructionWasEmpty
        )
            return setTemporarySequence(prevItem);

        if (
            sequence === 'oefening' &&
            mainExerciseStatus === 'started' &&
            !mainWasEmpty
        )
            return setTemporarySequence(prevItem);

        if (sequence === 'instructie' && priorKnowledgeWasEmpty) {
            return setSequence('voorkennis');
        }

        if (sequence === 'instructie' && !priorKnowledgeWasEmpty) {
            return setSequence('doel');
        }

        if (sequence === 'oefening' && instructionWasEmpty)
            return setSequence('instructie');

        if (sequence === 'oefening' && !instructionWasEmpty)
            return setSequence('voorkennis');

        if (sequence === 'succescriteria' && instructionWasEmpty)
            return setSequence('instructie');

        if (sequence === 'succescriteria' && mainWasEmpty)
            return setSequence('oefening');

        if (sequence === 'succescriteria' && !instructionWasEmpty)
            return setSequence('voorkennis');

        setSequence(prevItem);
    };

    useEffect(() => setKeyboardIsOpen(false), [sequence]);

    const nextSequence = () => {
        const keys = Object.keys(titles) as SequenceOptions[];
        const nextIndex = keys.indexOf(conditionalSequence) + 1;
        const nextItem = keys[nextIndex];

        if (temporarySequence === 'voorkennis') {
            setSequence('instructie');
            setTemporarySequence(null);
            return;
        }

        if (temporarySequence === 'instructie') {
            setSequence('oefening');
            setTemporarySequence(null);
            return;
        }

        if (temporarySequence === 'oefening') {
            setSequence('succescriteria');
            setTemporarySequence(null);
            return;
        }

        if (
            sequence === 'voorkennis' &&
            priorKnowledgeStatus === 'started' &&
            !priorKnowledgeWasEmpty &&
            temporarySequence
        )
            return setTemporarySequence(nextItem);

        if (
            sequence === 'instructie' &&
            instructionStatus === 'started' &&
            !instructionWasEmpty &&
            temporarySequence
        )
            return setTemporarySequence(nextItem);

        if (
            sequence === 'oefening' &&
            mainExerciseStatus === 'started' &&
            !mainWasEmpty &&
            temporarySequence
        )
            return setTemporarySequence(nextItem);

        if (sequence === 'doel' && priorKnowledgeWasEmpty)
            return setSequence('voorkennis');

        if (
            sequence === 'doel' &&
            priorKnowledgeIsFinishedCondition &&
            !instructionIsFinishedCondition
        )
            return setSequence('instructie');

        if (
            sequence === 'doel' &&
            priorKnowledgeIsFinishedCondition &&
            instructionIsFinishedCondition &&
            !mainIsFinishedCondition
        )
            return setSequence('oefening');

        if (sequence === 'doel' && mainIsFinishedCondition)
            return setSequence('succescriteria');

        if (sequence === 'voorkennis' && instructionWasEmpty)
            return setSequence('instructie');

        if (
            sequence === 'voorkennis' &&
            instructionIsFinishedCondition &&
            !mainIsFinishedCondition
        )
            return setSequence('oefening');

        if (sequence === 'voorkennis' && mainIsFinishedCondition)
            return setSequence('succescriteria');

        if (sequence === 'instructie' && mainWasEmpty)
            return setSequence('oefening');

        if (sequence === 'instructie' && mainIsFinishedCondition)
            return setSequence('succescriteria');

        setSequence(nextItem);
    };

    useEffect(() => setProgress(0), [sequence]);

    useEffect(() => {
        if (
            (priorKnowledgeStatus === 'finished' &&
                temporarySequence !== 'voorkennis') ||
            null
        ) {
            setTemporarySequence('voorkennis');
            setTemporarySequence(null);
        }
    }, [priorKnowledgeStatus]);

    useEffect(() => {
        if (
            (instructionStatus === 'finished' &&
                temporarySequence !== 'instructie') ||
            null
        ) {
            setTemporarySequence('instructie');
            setTemporarySequence(null);
        }
    }, [instructionStatus]);

    useEffect(() => {
        if (
            (mainExerciseStatus === 'finished' &&
                temporarySequence !== 'oefening') ||
            null
        ) {
            setTemporarySequence('oefening');
            setTemporarySequence(null);
        }
    }, [mainExerciseStatus]);

    const getExercisesProgress = () => {
        const exercises: ProgressType[] = [];

        // @ToDo: fix so that only a single entry can have progress

        if (priorKnowledgeIsFinished)
            exercises.push({
                done: getExerciseLength(priorKnowledgeExercise),
                total: getExerciseLength(priorKnowledgeExercise),
            });

        if (!priorKnowledgeIsFinished && !!priorKnowledgeExercise)
            exercises.push({
                done: progress,
                total: getExerciseLength(priorKnowledgeExercise),
            });

        if (instructionIsFinished)
            exercises.push({
                done: getExerciseLength(instructionExercise),
                total: getExerciseLength(instructionExercise),
            });

        if (!instructionIsFinished && !!instructionExercise)
            exercises.push({
                done:
                    !priorKnowledgeIsFinished && !!priorKnowledgeExercise
                        ? 0
                        : progress,
                total: getExerciseLength(instructionExercise),
            });

        if (mainIsFinished)
            exercises.push({
                done: getExerciseLength(mainExercise),
                total: getExerciseLength(mainExercise),
            });

        if (
            !mainIsFinished &&
            !!mainExercise &&
            !!instructionExercise &&
            (!instructionIsFinished || displayInstruction)
        )
            exercises.push({
                done: 0,
                total: getExerciseLength(mainExercise),
            });

        if (
            ((!!instructionExercise && instructionIsFinished) ||
                !instructionExercise) &&
            !!mainExercise &&
            !mainIsFinished &&
            !displayInstruction
        )
            exercises.push({
                done: progress,
                total: getExerciseLength(mainExercise),
            });

        return exercises;
    };

    return (
        <>
            <NavigationBox
                displayMode={displayMode}
                top={top}
                title={
                    isSummerTreatment // check if we need the Summer Treatment titles
                        ? summerTitles[conditionalSequence]
                        : titles[conditionalSequence]
                }
                text={
                    conditionalSequence !== 'afgerond'
                        ? homework?.[conditionalSequence]
                        : ''
                }
                subtitle={`Spellingwerk ${homework?.number}: `}
                exercise={treatment?.name}
                // @ts-ignore
                audio={homework?.[`${conditionalSequence}_audio`]}
                previous={
                    conditionalSequence === 'terugblik'
                        ? undefined
                        : previousSequence
                }
                next={
                    conditionalSequence === 'afgerond'
                        ? undefined
                        : temporarySequence === 'voorkennis' &&
                          (priorKnowledgeIsFinished || priorKnowledgeWasEmpty)
                        ? nextSequence
                        : temporarySequence === 'instructie' &&
                          (instructionIsFinished || instructionWasEmpty)
                        ? nextSequence
                        : temporarySequence &&
                          temporarySequence !== 'voorkennis' &&
                          temporarySequence !== 'instructie' &&
                          temporarySequence !== 'oefening'
                        ? nextSequence
                        : sequence === 'voorkennis' &&
                          !priorKnowledgeIsFinishedCondition
                        ? undefined
                        : sequence === 'instructie' &&
                          !instructionIsFinishedCondition
                        ? undefined
                        : sequence === 'oefening' && !mainIsFinishedCondition
                        ? undefined
                        : nextSequence
                }
                finishedButtons={sequence === 'afgerond' && 'homework'}
                ProgressBar={
                    ((sequence === 'voorkennis' && !priorKnowledgeWasEmpty) ||
                        (sequence === 'instructie' && !instructionWasEmpty) ||
                        (sequence === 'oefening' && !mainWasEmpty)) && (
                        <ProgressBar progressProps={getExercisesProgress()} />
                    )
                }
            />
            <Confetti active={sequence === 'afgerond'} />
        </>
    );
}
